import { useRoutes, Navigate, type RouteObject } from 'react-router-dom'
import type { ReactNode } from 'react'

export interface AppRoute {
  id: string
  name: string
  path?: string
  roles?: string[]
  element?: ReactNode
  icon?: ReactNode | string
  isPartOfLeftbar?: boolean
  index?: boolean
  children?: AppRoute[]
}

export interface DrawerItem {
  id: string
  text: string
  route: string
  icon?: ReactNode | string
  children: DrawerItem[]
}

/** Filter routes the current user can access based on their roles. */
export function filterRoutes(routes: AppRoute[], userRoles: string[]): AppRoute[] {
  const roleSet = new Set(userRoles)
  return routes
    .map((route) => {
      const hasAccess =
        !route.roles || route.roles.length === 0 || route.roles.some((r) => roleSet.has(r))
      if (!hasAccess) return null
      const filtered: AppRoute = { ...route }
      if (route.children) {
        filtered.children = filterRoutes(route.children, userRoles)
      }
      return filtered
    })
    .filter(Boolean) as AppRoute[]
}

/**
 * Convert AppRoute[] to react-router RouteObjects and render via useRoutes.
 * Unknown paths fall through to a catch-all that redirects to `fallbackPath`.
 */
export function RoutesMapper({
  routes,
  roles,
  fallbackPath,
}: {
  routes: AppRoute[]
  roles: string[]
  fallbackPath?: string
}) {
  const filteredRoutes = filterRoutes(routes, roles)

  function toRouteObjects(appRoutes: AppRoute[]): RouteObject[] {
    return appRoutes.map((route) => ({
      key: route.id,
      ...(route.index ? { index: true } : { path: route.path }),
      ...(route.children
        ? { children: toRouteObjects(route.children) }
        : { element: route.element }),
    })) as RouteObject[]
  }

  const routeObjects = toRouteObjects(filteredRoutes)

  // Redirect unknown paths to the first accessible route
  if (fallbackPath) {
    routeObjects.push({ path: '*', element: <Navigate to={fallbackPath} replace /> })
  }

  return useRoutes(routeObjects)
}

/**
 * Build sidebar nav items from routes accessible to the user.
 * Uses route.path directly — BrowserRouter basename handles the /operations prefix.
 */
export function getDrawerItems(routes: AppRoute[], userRoles: string[]): DrawerItem[] {
  const roleSet = new Set(userRoles)

  function processRoutes(appRoutes: AppRoute[]): DrawerItem[] {
    return appRoutes
      .map((route) => {
        if (!route.isPartOfLeftbar) return null
        const hasAccess =
          !route.roles || route.roles.length === 0 || route.roles.some((r) => roleSet.has(r))
        if (!hasAccess) return null

        const item: DrawerItem = {
          id: route.id,
          text: route.name,
          route: route.path ?? '/',
          icon: route.icon,
          children: route.children ? processRoutes(route.children) : [],
        }
        return item
      })
      .filter(Boolean) as DrawerItem[]
  }

  return processRoutes(routes)
}
